<template>

  <v-main class="auth-view" :class="{'dark': $vuetify.theme.dark}">

    <login-shader-background ref="shaderBg" v-if="!ci || (!ci.background_image_url && !ci.background_video_url)" />

    <template v-else>
      <video v-if="ci.background_video_url"
             :src="ci.background_video_url" muted autoplay loop class="background-video"
             playsinline></video>

      <div v-else-if="ci.background_image_url"
           class="background-image" :style="{ backgroundImage: 'url(' + ci.background_image_url + ')' }"></div>

      <div class="background-overlay"></div>
    </template>

    <v-container fluid fill-height style="z-index: 10;">
      <v-layout align-center justify-center>
        <v-flex xs12 sm10 md6 lg6 :style="{ maxWidth: server_check_state === 'ok' ? '300px' : '400px', transition: 'max-width 0.4s ease' }">

          <v-card elevation="0">

            <v-card-text>
              <v-form ref="form" @submit.prevent="login()">

                <v-expand-transition>
                  <div v-if="server_check_state !== 'ok'" style="max-width: 100px; margin: 0 auto 20px;">
                    <app-logo></app-logo>
                  </div>
                </v-expand-transition>

                <div v-if="!gravatar_url && ci && ci.logo_url" class="text-center mb-6">
                  <img :src="ci.logo_url" style="max-width: 100%; max-height: 60px; object-fit: contain;" alt="Logo" />
                </div>
                <div v-else-if="!gravatar_url && ci && ci.company_name" class="text-center mb-6"
                     :style="{ color: $vuetify.theme.dark ? '#fff' : 'rgba(0,0,0,0.87)', fontSize: '14px', fontWeight: '500', letterSpacing: '0.03em' }">
                  {{ ci.company_name }}
                </div>

                <div v-if="session.servers.length == 0 || session.server_selector === true">
                  <login-server-field
                      :value="credentials.server"
                      :servers="session.servers"
                      :forced-server="forced_server"
                      :server-domain="server_domain"
                      :disabled="session.state === 'authenticating'"
                      @input="val => credentials.server = val"
                      @remove-server="session.removeServer($event)"
                      @submit="login()"
                  />
                </div>

                <v-expand-transition>
                  <div v-if="server_check_state === 'checking'" class="mb-3 d-flex align-center justify-center" style="color: #888; font-size: 13px;">
                    <v-progress-circular indeterminate size="14" width="2" color="grey" class="mr-2"></v-progress-circular>
                    {{ $t('login.label.connecting') }}
                  </div>
                </v-expand-transition>

                <v-expand-transition>
                  <v-alert v-if="server_check_state === 'error'" type="error" dense text class="mb-3">
                    {{ $t('login.label.no_server_found') }}
                  </v-alert>
                </v-expand-transition>

                <v-expand-transition>
                  <div v-if="server_check_state === 'ok'">

                    <login-user-avatar
                        :gravatar-url="gravatar_url"
                        :display-name="display_name"
                        :login="credentials.login"
                        :disabled="session.state === 'authenticating'"
                        @update:login="val => { credentials.login = val; loginError = null; }"
                        @keyup-enter="login()"
                        @forget="forgetUser()"
                    />

                    <v-text-field
                        :disabled="session.state === 'authenticating'"
                        required outlined dense hide-details class="mb-3"
                        :label="$t('login.label.password')"
                        :type="password_type"
                        :append-icon="password_append_icon"
                        v-model="credentials.password"
                        @keyup.enter="login()"
                        @input="loginError = null"
                        :clearable="true"
                        autocomplete="current-password"
                        @click:append="toggle_password_field()"
                    ></v-text-field>

                    <v-checkbox
                        v-model="credentials.remember_me"
                        :label="$t('login.label.remember_me')"
                        :disabled="session.state === 'authenticating'"
                        hide-details class="mt-0 mb-3"
                    ></v-checkbox>

                    <v-alert
                        v-if="loginError"
                        type="error" dense text class="mb-3"
                        dismissible @input="loginError = null"
                    >{{ loginError }}</v-alert>

                    <v-btn
                        color="primary" depressed block
                        @click="login()" :loading="session.state === 'authenticating'"
                        :disabled="(credentials.login || '').length < 3 || (credentials.password || '').length < 3">
                      {{ $t('login.label.sign_in') }}
                    </v-btn>

                  </div>
                </v-expand-transition>

              </v-form>
            </v-card-text>

            <v-card-text v-if="server_check_state === 'ok'" class="d-flex justify-center pt-0">
              <v-btn text small :to="{ name: 'forgot-password' }" class="text-none" color="grey darken-1">
                {{ $t('login.label.forgot_password') }}
              </v-btn>
            </v-card-text>

            <v-card-actions class="pt-0 px-4">
              <span style="font-size: 12px;">© {{ new Date().getFullYear() }} <a href="https://imbaa.de">IMBAA GmbH</a></span>
              <v-spacer></v-spacer>

              <v-btn icon small @click="session.toggleServerSelector()" v-if="session.servers.length !== 0">
                <v-icon small>mdi-briefcase-arrow-up-down</v-icon>
              </v-btn>

              <v-menu top offset-y>
                <template #activator="{ on }">
                  <v-btn icon small v-on="on">
                    <v-badge v-if="session.scanner_mode" color="primary" dot overlap>
                      <v-icon small>mdi-cellphone</v-icon>
                    </v-badge>
                    <v-icon v-else small>mdi-cellphone</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="ma-0 pa-0">
                  <v-list-item @click="session.setScannerMode(null)" :input-value="session.scanner_mode === null">
                    <v-list-item-icon><v-icon small>mdi-monitor</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('login.label.interface-normal') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="session.setScannerMode('storage')" :input-value="session.scanner_mode === 'storage'">
                    <v-list-item-icon><v-icon small>mdi-warehouse</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('login.label.interface-storage') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="session.setScannerMode('pos')" :input-value="session.scanner_mode === 'pos'">
                    <v-list-item-icon><v-icon small>mdi-cash-register</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('login.label.interface-pos') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <theme-switcher type="icon" small></theme-switcher>
              <lang-switch small style="margin: 0 auto; display: inline-block;"></lang-switch>
            </v-card-actions>

          </v-card>

          <login-footer
              :client-version="session.client_version"
              :server-check-ok="server_check_state === 'ok'"
          />

        </v-flex>
      </v-layout>
    </v-container>

    <two-factor-registration-form
        v-if="session.state === '2fa_method_registration_start'"
        @dismiss="handle2FADismiss"
        @completed="close2FAAlert"
    />

  </v-main>

</template>


<script>

import {sessionStore} from '@/store/pinia/sessionStore'
import {twoFactorAuthStore} from '@/store/pinia/twoFactorAuthStore'
import AppLogo from '@/components/_app/AppLogo.vue'
import ThemeSwitcher from '@/components/_app/theme_switcher.vue'
import LangSwitch from '@/components/_app/system_language_selector.vue'
import TwoFactorRegistrationForm from '@/components/_security/TwoFactorRegistrationForm.vue'
import LoginShaderBackground from './LoginShaderBackground.vue'
import LoginServerField from './LoginServerField.vue'
import LoginUserAvatar from './LoginUserAvatar.vue'
import LoginFooter from './LoginFooter.vue'

export default {

  name: 'Login',
  components: {
    AppLogo,
    ThemeSwitcher,
    LangSwitch,
    TwoFactorRegistrationForm,
    LoginShaderBackground,
    LoginServerField,
    LoginUserAvatar,
    LoginFooter,
  },

  setup() {
    const session = sessionStore()
    const twoFactorAuth = twoFactorAuthStore()
    return { session, twoFactorAuth }
  },

  data() {
    return {
      credentials: {
        server: null,
        login: '',
        password: '',
        remember_me: false,
      },
      loginError: null,
      password_type: 'password',
      forced_server: process.env.VUE_APP_FORCED_SERVER,
      server_domain: process.env.VUE_APP_SERVER_DOMAIN || null,
      server_check_state: null, // null | 'checking' | 'ok' | 'error'
      server_check_timer: null,
      ci: null,
      gravatar_url: null,
      display_name: null,
    }
  },

  computed: {
    password_append_icon() {
      return this.password_type === 'text' ? 'mdi-eye' : 'mdi-eye-off'
    },
  },

  methods: {

    resolveEndpoint(server) {
      if (!server) return null
      if (process.env.VUE_APP_FORCED_SERVER) return server
      if (process.env.VUE_APP_SERVER_DOMAIN && !server.includes('.')) {
        return 'https://' + server + '.' + process.env.VUE_APP_SERVER_DOMAIN
      }
      return /^https?:\/\//.test(server) ? server : 'https://' + server
    },

    async checkServer(server) {
      const endpoint = this.resolveEndpoint(server)
      if (!endpoint) return
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 5000)
        const response = await fetch(endpoint + '/auth/server-info', { signal: controller.signal })
        clearTimeout(timeout)
        if (response.ok) {
          const data = await response.json()
          this.ci = data?.payload?.ci || null
          this.server_check_state = 'ok'
          if (this.session.server_selector) {
            this.session.server_selector = false
            localStorage.setItem('pling:show_server_selector', 'false')
          }
        } else {
          this.server_check_state = 'error'
        }
      } catch {
        // CORS blocks the response body but the server may still exist.
        // Try an opaque no-cors request — if it succeeds, the server is reachable.
        try {
          const controller = new AbortController()
          const timeout = setTimeout(() => controller.abort(), 5000)
          const probe = await fetch(endpoint + '/auth/server-info', { mode: 'no-cors', signal: controller.signal })
          clearTimeout(timeout)
          // no-cors returns an opaque response (type 'opaque', status 0) on success
          if (probe.type === 'opaque') {
            this.ci = null // can't read CI data through opaque response
            this.server_check_state = 'ok'
            if (this.session.server_selector) {
              this.session.server_selector = false
              localStorage.setItem('pling:show_server_selector', 'false')
            }
          } else {
            this.server_check_state = 'error'
          }
        } catch {
          this.server_check_state = 'error'
        }
      }
    },

    lookupGravatar(username) {
      if (!username || !this.credentials.server) {
        this.gravatar_url = null
        this.display_name = null
        return
      }
      this.gravatar_url = localStorage.getItem(`pling:gravatar:${this.credentials.server}:${username}`) || null
      this.display_name = localStorage.getItem(`pling:display_name:${this.credentials.server}:${username}`) || null
    },

    forgetUser() {
      if (this.credentials.login && this.credentials.server) {
        localStorage.removeItem(`pling:gravatar:${this.credentials.server}:${this.credentials.login}`)
        localStorage.removeItem(`pling:display_name:${this.credentials.server}:${this.credentials.login}`)
        localStorage.removeItem('pling.credentials.last_login')
      }
      this.gravatar_url = null
      this.display_name = null
      this.credentials.login = ''
    },

    toggle_password_field() {
      this.password_type = this.password_type === 'password' ? 'text' : 'password'
    },

    async login() {
      this.loginError = null
      this.credentials.server = this.credentials.server.replace(/\s/g, '')
      this.credentials.login = this.credentials.login.replace(/\s/g, '')

      // Trigger star map outro
      if (this.$refs.shaderBg) this.$refs.shaderBg.playOutro()

      try {
        const result = await this.session.signIn(this.credentials)

        if (result?.loginError) {
          this.loginError = result.loginError
          if (this.$refs.shaderBg) this.$refs.shaderBg.playIntro()
          return
        }

        if (this.session.state === 'awaiting_2fa') {
          if (!this.twoFactorAuth.available2FAMethods) return
          if (this.twoFactorAuth.available2FAMethods.length <= 1) {
            this.twoFactorAuth.selected2FAMethod = this.twoFactorAuth.available2FAMethods[0]
          }
          await this.$router.push({ name: '2fa-verification' })
        }
      } catch (e) {
        if (e?.response?.status === 429) {
          this.loginError = e?.response?.data?.error || this.$t('login.label.too_many_attempts')
        } else {
          this.loginError = e?.response?.data?.error || this.$t('2fa.error_generic')
        }
        if (this.$refs.shaderBg) this.$refs.shaderBg.playIntro()
      }
    },

    async close2FAAlert() {
      try {
        const response = await this.$http.post('/auth/2fa/complete-registration', {
          auth_token: this.session.auth_token
        })
        if (response.data.payload === true) {
          await this.session.processSessionAuthentication()
        }
      } catch (error) {
        console.error('Failed to complete registration:', error)
        this.session.state = 'ready'
      }
    },

    async handle2FADismiss() {
      await this.session.processSessionAuthentication()
    },

  },

  watch: {
    'credentials.server'(val) {
      clearTimeout(this.server_check_timer)
      this.server_check_state = null
      this.ci = null
      if (!val) return
      this.server_check_state = 'checking'
      this.server_check_timer = setTimeout(() => this.checkServer(val), 600)
    },
    'credentials.login'(val) {
      this.lookupGravatar(val)
    },
    'session.server_selector'(val) {
      if (val) {
        clearTimeout(this.server_check_timer)
        this.server_check_state = null
        this.ci = null
      } else if (this.credentials.server) {
        this.server_check_state = 'checking'
        this.checkServer(this.credentials.server)
      }
    },
  },

  mounted() {
    this.credentials.server = this.session.server

    const last_login = localStorage.getItem('pling.credentials.last_login')
    if (last_login) {
      this.credentials.login = last_login
      this.lookupGravatar(last_login)
    }

    if (this.forced_server) {
      this.credentials.server = this.forced_server
    }

    // URL parameter overrides — e.g. ?company=acme&user=john
    const params = new URLSearchParams(window.location.search)
    const companyParam = params.get('company') || params.get('server')
    if (companyParam) {
      this.credentials.server = companyParam
      if (this.session.server_selector) {
        this.session.server_selector = false
        localStorage.setItem('pling:show_server_selector', 'false')
      }
    }

    const userParam = params.get('user') || params.get('username')
    if (userParam) {
      this.credentials.login = userParam
      this.lookupGravatar(userParam)
    }
  },

}

</script>


<style lang="scss" scoped>
.background-video {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.background-overlay {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  pointer-events: none;
}
</style>
